import React from 'react';
import { Icons } from '@usepepper/sprout-kit-ui';
import { RemoteIcon } from '@/experiences/shared/components/remote-icon';
import { theme } from '@/styles';
import { View } from 'react-native';

export interface MenuItemIconConfig {
  /** Last-resort node, tinted and sized by the caller: a root `fill`
   *  overrides the asset's `fill="none"` and flood-fills stroke-based icons
   *  into solid shapes (.svgrrc maps the asset color to the `fill` prop). */
  icon?: React.ReactNode;
  /** Sprout Kit icon name. Preferred — the only icon that adapts to a
   *  partner-themed nav rail. */
  iconName?: string;
  /** Remote brand image, rendered at its own fixed colors. */
  iconUrl?: string;
  id: string;
}

const NAMED_ICONS = Icons as Record<
  string,
  React.ComponentType<{ color?: string; size?: number }>
>;

/**
 * iconName comes from backend GA config, so the own-property guard is
 * mandatory: Icons has Object.prototype intact, and an unguarded lookup
 * resolves names like "constructor" to non-components that crash the render.
 */
export const resolveNamedIcon = (iconName?: string) =>
  iconName && Object.prototype.hasOwnProperty.call(NAMED_ICONS, iconName)
    ? NAMED_ICONS[iconName]
    : undefined;

interface Props {
  // Color that's only applied to named icons
  color?: string;
  item: MenuItemIconConfig;
  size: number;
}

/** Named icons are Tamagui components and require a SproutKitProvider
 *  ancestor, mounted once at the app root. */
export const MenuItemIcon: React.FC<Props> = ({ color, item, size }) => {
  const NamedIcon = resolveNamedIcon(item.iconName);
  if (NamedIcon) {
    return (
      <View testID={`menu-item-icon-named-${item.id}`}>
        <NamedIcon color={color ?? theme.colors.primary} size={size} />
      </View>
    );
  }

  if (item.iconUrl) {
    // RemoteIcon: SVG urls render through expo-image (RN Image silently drops
    // SVG on native).
    return (
      <RemoteIcon
        size={size}
        testID={`menu-item-icon-image-${item.id}`}
        uri={item.iconUrl}
      />
    );
  }

  if (item.icon) {
    return (
      <View testID={`menu-item-icon-fallback-${item.id}`}>{item.icon}</View>
    );
  }

  return null;
};
